import {Component, ElementRef, HostListener, ViewChild} from '@angular/core';
import {SpThemingService} from '../../../services/sp-theming/sp-theming.service';
import {MatDialog} from '@angular/material/dialog';
import {SpModalProjectListShareComponent} from './sp-modal-project-list-share/sp-modal-project-list-share.component';
import {SpModalProjectListAddToFolderComponent} from './sp-modal-project-list-add-to-folder/sp-modal-project-list-add-to-folder.component';
import {Project} from '../../../models/project';
import {environment} from '../../../../environments/environment';
import {User} from '../../../models/user';
import {Folder} from '../../../models/folder';
import {SpModalProjectListProjectDetailsComponent} from './sp-modal-project-list-project-details/sp-modal-project-list-project-details.component';
import {SpModalProjectListProjectDeleteComponent} from './sp-modal-project-list-project-delete/sp-modal-project-list-project-delete.component';
import {SpModalProjectListFolderDeleteComponent} from './sp-modal-project-list-folder-delete/sp-modal-project-list-folder-delete.component';
import {ApiProjectService} from '../../../services/sp-api/sp-api-project/api-project.service';
import {ApiFolderService} from '../../../services/sp-api/sp-api-folder/api-folder.service';
import {CrawlTrackerService} from '../../../services/sp-crawl-tracker/crawl-tracker.service';
import {Router} from '@angular/router';
import {AnyFolder, ProjectManagerService, ProjectSortType} from '../../../services/sp-project/project-manager.service';
import {isPlatformFolder} from '../../../models/platform-folder';
import {FolderActionsService} from '../../../services/sp-folder/folder-actions.service';
import {LoaderService} from '../../../services/sp-loading/loader.service';
import {ChartService} from '../../../services/sp-chart/chart.service';
import {NavigationService} from '../../../services/sp-navigation/navigation.service';
import {AlertService} from '../../../services/sp-alert/alert.service';
import { FacebookLinkService } from '../../../services/sp-facebook/facebook-link.service';
import {debounceTime, switchMap} from 'rxjs/operators';
import {LoggerService} from '../../../services/sp-logger/logger.service';
import {DashboardComponent} from '../../../classes/components/dashboard-component';
import {EMPTY, firstValueFrom, Subject} from 'rxjs';
import {ArrayUtils} from '../../../utils/array-utils';
import {ApiDataService} from '../../../services/sp-api/sp-api-data/api-data.service';
import {SpModalInfoComponent} from '../../sp-modal/sp-modal-info/sp-modal-info.component';
import { CrawlStatus } from 'src/app/models/crawl-status';
import {SpModalCrawlConfirmationComponent} from '../../sp-modal/sp-modal-crawl-confirmation/sp-modal-crawl-confirmation.component';
import {TrackerService} from '../../../services/sp-tracker/tracker.service';
import {Subscription} from '../../../models/subscription';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag.service';
import { DiReportNavigatorService } from '../../../services/sp-navigation/di-report-navigator.service';

@Component({
  selector: 'app-sp-project-list',
  templateUrl: './sp-project-list.component.html',
  styleUrls: ['./sp-project-list.component.scss']
})
export class SpProjectListComponent extends DashboardComponent {
  /// Variable declarations
  // View
  private isViewActive = false;
  private lockScrollListener = false;

  // Projects
  projectReadyCount = 0;
  sortType: ProjectSortType = ProjectSortType.CREATION_DATE;

  // Folders
  filteredFolders: AnyFolder[] = [];

  // Loader
  hideScrollCard = false;

  // Form/Input
  filterText: string = "";
  titleEditing: HTMLInputElement[] = [];
  isInsideName = false;
  allUsersInProject: string;
  private openPopup = false;

  // Facebook
  facebookConnected = false;

  // Permissions
  canCreateProject = false;
  hasEnoughCredits = false;
  canAccessDashboard = false;
  canExportToMeta = false;
  canSeeCustomFolder = false;
  private adAccountBypass = false;

  get newProjectTooltip(): string {
    if (!this.hasEnoughCredits) {
      return 'You have no credits left, if you want additional credits or to upgrade your offer, contact us.';
    }
    if (!this.facebookConnected) {
      return 'Your Facebook account is not connected. Click the link below to connect it.';
    }
    if (this.sessionProvider.activeSession?.user.ad_accounts.length === 0) {
      return 'You don\'t have any ads account. Go to My Account (at the top right of the screen) and click on the Update token button in the My Facebook account box.';
    }
    return 'Create a new project';
  }

  /**
   *
   * @param theming service - theming
   * @param dialog service - mat dialog
   * @param apiProject service - api project
   * @param apiFolder service - api folder
   * @param apiData
   * @param navigation service - navigation
   * @param crawlTracker service - crawl tracker
   * @param projectManager service - project manager
   * @param auth service - authentication
   * @param loader service - global loader
   * @param alert service - alert
   * @param facebook service - facebook
   * @param logger service - logging
   * @param router angular router
   */
  constructor(
    theming: SpThemingService,
    private dialog: MatDialog,
    private apiProject: ApiProjectService,
    private apiFolder: ApiFolderService,
    private folderActions: FolderActionsService,
    private apiData: ApiDataService,
    public navigation: NavigationService,
    public crawlTracker: CrawlTrackerService,
    public projectManager: ProjectManagerService,
    public loader: LoaderService,
    private alert: AlertService,
    private facebook: FacebookLinkService,
    private logger: LoggerService,
    private tracker: TrackerService,
    public router: Router,
    private featureFlagService: FeatureFlagsService,
    sessionProvider: SessionProviderService,
    public diReportNavigator: DiReportNavigatorService
  ) {
    super(theming, sessionProvider);
  }

  @ViewChild('title', {static: true}) projectTitles: HTMLInputElement[];
  @ViewChild('scroll', {read: ElementRef}) public scroll: ElementRef;

  async afterInit() {
    this.isViewActive = true;

    this.registerEvents();

    if (!this.sessionProvider.activeSession) return;

    await this.permissionCheck();
    await this.facebookConnectionCheck();

    // Check if first project creation status has been changed
    if (!this.sessionProvider.activeSession.user.first_project_created && this.projectManager.projectCreatedCount > 0) {
      this.sessionProvider.activeSession.user.first_project_created = true;
    }

    // Sending an event for metrics
    if (!this.canCreateProject) {
      let reason = 'unknown';

      if (!this.canBypassProjectCreationRequirements()) {
        if (!this.facebookConnected) {
          reason = 'facebook_not_connected';
        } else if (this.sessionProvider.activeSession.user.ad_accounts.length === 0) {
          reason = 'no_ad_accounts';
        }
      }

      this.tracker.track('report_list_creation_unavailable_detected', {reason});
    }

    // Checking if folders or at least 1 chunk of projects are loaded
    await this.initProjects();
  }

  private async initProjects() {
    if (this.projectManager.projects.length < ProjectManagerService.PROJECT_CHUNK_LIMIT) {
      this.loader.load('Loading projects');
      this.logger.debug("Project loaded list empty, loading one chunk");
      this.projectManager.loadProjectChunk(this.sortType, this.filterText, this.projectAndFolderTypeFiltering).then();
    } else {
      this.projectReadyCount = this.projects.length;
      this.hideScrollCard = this.projectCount >= this.projects.length || this.filterText.length > 0;
    }

    if (this.projectManager.folders.length == 0) {
      this.filteredFolders = await this.projectManager.loadFolders();
    } else {
      this.filteredFolders = this.projectManager.folders;
    }
  }

  beforeDestroy() {
    this.isViewActive = false;
    this.projectManager.selectFolder(undefined);
  }

  private async permissionCheck() {
    if (this.sessionProvider.activeSession) {
      this.adAccountBypass = await this.sessionProvider.activeSession.hasPermission('adaccount.bypass');
      this.canAccessDashboard = await this.sessionProvider.activeSession.hasPermission('dashboard.access');
      this.canExportToMeta = await this.sessionProvider.activeSession.hasPermission('targeting.export');
      this.canSeeCustomFolder = await this.sessionProvider.activeSession.hasPermission('crawl.custom');

      if (this.canBypassProjectCreationRequirements()) this.canCreateProject = true;
      this.hasEnoughCredits = Subscription.totalCredits(this.sessionProvider.activeSession.user.company.subscription) > 0;
    }
  }

  private async facebookConnectionCheck() {
    if (this.canBypassProjectCreationRequirements()) {
      this.canCreateProject = true;
      this.facebookConnected = false;
    } else {
      const co = await this.facebook.isLogged();
      this.facebookConnected = co;
      this.canCreateProject = co && this.sessionProvider.activeSession.user.ad_accounts.length > 0;
    }
  }

  private canBypassProjectCreationRequirements() {
    return this.freemium || this.trial || this.adAccountBypass;
  }

  @HostListener('window:scroll')
  loadProjects() {
    if (this.canLoadProject()) {
      const pos = (document.documentElement.scrollTop || document.body.scrollTop) + document.documentElement.offsetHeight;
      const max = document.documentElement.scrollHeight;

      if (pos >= (max - 100)) {
        this.lockScrollListener = true;
        this.projectManager.loadProjectChunk(this.sortType, this.filterText, this.projectAndFolderTypeFiltering).then();
      }
    }
  }

  private onProjectChunkLoaded() {
    if (this.loader.loading) this.loader.loaded();
    this.hideScrollCard = this.projects.length >= this.projectCount;
  }

  /**
   * Execute search filter on projects
   * @param searchText
   * @private
   */
  private onProjectSearchConfirm(searchText: string) {
    this.projectReadyCount = 0;
    this.projectManager.unloadProjectChunks();
    this.projectManager.loadProjectChunk(this.sortType, searchText, this.projectAndFolderTypeFiltering).then();
  }

  /**
   * Execute type filtering on projects
   * @param types
   * @private
   */
  private async onProjectTypeFilterConfirm(types: string[]) {
    if (!ArrayUtils.isSameContentIgnoringOrder(this.projectAndFolderTypeFiltering, types)) {
      let includingFolder = false;
      this.projectAndFolderTypeFiltering = types;
      if (this.projectAndFolderTypeFiltering.includes('project') || this.projectAndFolderTypeFiltering.includes('project_shared')) {
        this.hideScrollCard = false;
      }
      this.projectManager.reloadProjectChunks(this.filterText, this.projectAndFolderTypeFiltering);
      if (types.includes('folder')) {
        includingFolder = true;
        await this.projectManager.loadFolders();
      }
      if (types.includes('folder_custom')) {
        includingFolder = true;
        await this.projectManager.loadFolders(true);
      }

      if (includingFolder) {
        this.filterUserFolders();
      }
    }

    this.projectManager.projectTypeFilterLoading.next(false);
  }

  /**
   * Duplicate a project
   * @param project the project to duplicate
   * @private
   */
  private projectDuplicate(project: Project) {
    this.apiProject.projectDuplicate(project).then(() => {
      const sub = this.sessionProvider.activeSession.user.company.subscription;
      Subscription.substractCredit(sub);

      this.tracker.track('report_list_report_duplicated', {
        report_id: project.id,
        report_name: project.name,
      });

      if (Subscription.totalCredits(sub) <= 0) {
        this.tracker.track('subscription_credits_exhausted', {});
      }
      this.projectManager.reloadProjectChunks(this.filterText, this.projectAndFolderTypeFiltering);
    }).catch(error => {
      this.handleTargetSpecError(error);
    });
  }

  async recrawlProject(project: Project) {
    const newCrawl = await this.featureFlagService.isEnabled('new_crawl');
    this.apiProject.projectCrawl(project, undefined, undefined, newCrawl).then(() => {
      Subscription.substractCredit(this.sessionProvider.activeSession.user.company.subscription);
      this.tracker.trackCreditsUsed(1, 'report_refreshed', this.sessionProvider.activeSession.user.company.subscription);
      this.tracker.track('report_list_report_refreshed', {
        project_name: project.name,
      });

      project.audience_target.latest_crawl = null;
      project.audience_target.status = null;
      project.audience_bench.latest_crawl = null;
      project.audience_bench.status = null;
      setTimeout(async () => {
        await this.projectManager.reloadProjectInfo(project);
        await this.crawlTracker.addProject(project);
      }, 5000);
    }).catch(error => {
      this.handleTargetSpecError(error);
    });
  }
  recrawlFolder(folder: Folder) {
    this.apiProject.projectCrawlBatch(folder).then(() => {
      this.alert.notify('Launched!', 'The projects inside this folder has been launched for crawl!', 'info');
    });
  }
  criterionAvailable(project: Project) {
    // Use isProjectDataAccessible to allow access during segmentation crawls
    // (criteria data is available, only segments are being built)
    return this.projectManager.isProjectDataAccessible(project);
  }

  async onFolderTitleUpdate(title: string, folder: AnyFolder): Promise<void> {
    if (title !== folder.name) {
      await this.folderActions.renameFolder(folder, title);
    }
  }
  /** "+N on the Audiense platform": the reports of other products a platform folder holds, which only the platform shows. */
  otherProductsLabel(folder: AnyFolder): string | null {
    return isPlatformFolder(folder) && folder.otherProductsCount > 0
      ? `+${folder.otherProductsCount} on the Audiense platform`
      : null;
  }

  editTitle(title: HTMLInputElement) {
    if (!this.titleEditing.includes(title)) {
      this.titleEditing.push(title);
    }

    setTimeout(() => {
      title.focus();
    }, 10);
  }

  isTitleEditable(title: HTMLInputElement) {
    return this.titleEditing.includes(title);
  }

  onProjectTitleUpdate(title: HTMLInputElement, project: Project) {
    this.titleEditing.splice(this.titleEditing.indexOf(title), 1);

    if (project.name !== title.value) {
      project.name = title.value;
      this.apiProject.updateOne(project).then(p => {
        project = p;
      });
    }
  }

  isProjectCrawling(project: Project) {
    return !this.projectManager.isProjectCompletelyReady(project);
  }

  buttonLoadProject(){
    this.projectManager.loadProjectChunk(this.sortType, this.filterText, this.projectAndFolderTypeFiltering).then();
  }

  selectFolder(folder: Folder) {
    this.filterText = '';
    this.lockScrollListener = true;
    this.hideScrollCard = false;
    this.projectReadyCount = 0;

    this.projectManager.selectFolder(folder);
    this.projectManager.reloadProjectChunks(this.filterText, this.projectAndFolderTypeFiltering);
    setTimeout(() => {
      this.lockScrollListener = false;
    }, 200);
  }

  openAddToFolderDialog(project: Project): void {
    this.openPopup = true;
    const sub = this.dialog.open(SpModalProjectListAddToFolderComponent, {
      minHeight: '20rem',
      minWidth: '50rem',
      maxHeight: '30rem',
      maxWidth: '60rem',
      data: { project, folders: this.projectManager.folders }
    }).afterClosed().subscribe(() => {
      this.openPopup = false;
      sub.unsubscribe();
    });
  }

  newProjectRedirect(){
    if(this.projectManager.openedFolder) {
      this.router.navigate(['/projects/create/select'], {
        queryParams: {
          folderId: this.projectManager.openedFolder.id || null
        }
      });
    } else {
      this.router.navigateByUrl('/projects/create/select').then();
        }
  }

  async removeProjectFromFolder(project: Project): Promise<void> {
    await this.folderActions.removeProject(this.projectManager.openedFolder, project);
    this.projectManager.removeLocalProject(project);
  }

  openProjectDetailsDialog(project: Project): void {
    this.openPopup = true;
    const sub = this.dialog.open(SpModalProjectListProjectDetailsComponent, {
      autoFocus: false,
      minHeight: '30rem',
      minWidth: '40rem',
      maxHeight: '60rem',
      maxWidth: '60rem',
      data: project
    }).afterClosed().subscribe(() => {
      this.openPopup = false;
      sub.unsubscribe();
    });
  }

  openDeleteProjectDialog(project: Project): void {
    this.openPopup = true;
    const dialogRef = this.dialog.open(SpModalProjectListProjectDeleteComponent, {
      autoFocus: false,
      minHeight: '10rem',
      minWidth: '30rem',
      maxHeight: '30rem',
      maxWidth: '60rem',
      data: project
    });

    const sub = dialogRef.afterClosed().subscribe(deleted => {
      if (deleted) { this.projectManager.removeLocalProject(project); }
      this.openPopup = false;
      sub.unsubscribe();
    });
  }

  openRefreshProjectCreditsDialog(project: Project): void {
    this.openPopup = true;
    const sub = this.dialog.open(SpModalCrawlConfirmationComponent, {
      autoFocus: false,
      maxWidth: '450px',
      data: {creditCount: 1}
    }).afterClosed().subscribe(result => {
      this.openPopup = false;
      if (result) {
        this.recrawlProject(project);
      }
      sub.unsubscribe();
    });
  }

  openRefreshFolderDialog(folder: Folder) {
    this.openPopup = true;
    firstValueFrom(this.dialog.open(SpModalCrawlConfirmationComponent, {
      autoFocus: false,
      maxWidth: '450px'
    }).afterClosed()).then(result => {
      if (result) {
        this.recrawlFolder(folder);
      }
    }).finally(() => this.openPopup = false);
  }

  openDuplicateProjectCreditsDialog(project: Project): void {
    this.openPopup = true;
    const sub = this.dialog.open(SpModalCrawlConfirmationComponent, {
      autoFocus: false,
      maxWidth: '450px',
      data: {creditCount: 1}
    }).afterClosed().subscribe(result => {
      this.openPopup = false;
      if (result) { this.projectDuplicate(project); }
      sub.unsubscribe();
    });
  }

  openDeleteFolderDialog(folder: AnyFolder): void {
    this.openPopup = true;
    const sub = this.dialog.open(SpModalProjectListFolderDeleteComponent, {
      autoFocus: false,
      minHeight: '10rem',
      minWidth: '30rem',
      maxHeight: '30rem',
      maxWidth: '60rem',
      data: folder
    }).afterClosed().subscribe(projects => {
      this.openPopup = false;
      if (projects) {
        if (this.projectManager.openedFolder && this.projectManager.openedFolder.id === folder.id) {
          this.projectManager.selectFolder(undefined);
        }
        this.projectManager.removeFolder(folder);
        this.filterUserFolders();
        // Its projects were hidden from the root list while it existed.
        if (isPlatformFolder(folder)) {
          this.projectManager.reloadProjectChunks(this.filterText, this.projectAndFolderTypeFiltering);
        }
      }
      sub.unsubscribe();
    });
  }

  openShareDialog(project: Project): void {
    this.openPopup = true;
    const dialogRef = this.dialog.open(SpModalProjectListShareComponent, {
      minHeight: '10rem',
      minWidth: '30rem',
      maxHeight: '30rem',
      maxWidth: '60rem',
      data: project});

    const sub = dialogRef.afterClosed().subscribe(users => {
      this.openPopup = false;
      if (users) {
        if (users.selected && users.selected.length > 0) {
          project.shared_users = project.shared_users.concat(users.selected);
        }
        if(users.unselected.length > 0) {
          users.unselected.forEach(x => {
            project.shared_users.splice(project.shared_users.indexOf(x),1)
          })
        }
      }

      sub.unsubscribe();
    });
  }

  goToMetaExport(project: Project) {
    const params: any = {
      'name': project.name,
      'targeting': project.audience_target.target_spec,
      'type': 'project'
    };

    if (project.audience_target.type == 'custom' || project.audience_target.type == 'fanpage') {
      const extra = JSON.parse(project.audience_target.data_extra);
      params.adAccountId = extra.accountId;
      params.adAccountName = extra.accountName;
      params.audienceType = project.audience_target.type;
    }

    this.router.navigate(['targeting'], {queryParams: params}).then();
  }

  dateToString(date: Date) {
    return new Date(date).toLocaleDateString();
  }

  getProjectUsers(project: Project) {
    const users: User[] = [project.user];
    this.allUsersInProject = users.concat(project.shared_users).slice(3,project.shared_users.length + 1).map(u => u.first_name + " " + u.last_name).join(", ");
    return users.concat(project.shared_users);
  }

  toNumberFormat(value: number) {
    return ChartService.separator(value);
  }

  userInitials(user: User) {
    return user.first_name[0] + user.last_name[0];
  }

  private registerEvents() {
    const projectChunkSub = this.projectManager.projectChunkListener.subscribe(projects => {
      this.lockScrollListener = false;
      this.onProjectChunkLoaded();
    });

    const projectCriteriaDoneSub = this.crawlTracker.crawlCriteriaDoneNotifier.subscribe(() => {
      this.projectReadyCount++;
    });

    const inputSub = this.projectManager.projectListSearchListener.pipe(
      debounceTime(500),
      switchMap(searchText => {
        if (this.filterText.trim() !== searchText?.trim()) {
          this.filterText = searchText ?? '';
          this.filterUserFolders();
          this.onProjectSearchConfirm(searchText);
        }
        return EMPTY;
      })
    ).subscribe();

    const typeFilterSub = this.projectManager.projectTypeFilterListener.subscribe(async types => {
      await this.onProjectTypeFilterConfirm(types);
    });

    const newFolderSub = this.projectManager.newFolderListener.subscribe(() => {
      this.filterUserFolders();
    });

    const authSub = this.sessionProvider.sessionChangeListener.subscribe(session => {
      if (session) {
        this.initProjects();
      }
    });

    this.addSubscription(projectChunkSub, projectCriteriaDoneSub, inputSub, typeFilterSub, newFolderSub, authSub);
  }

  private filterUserFolders() {
    if (this.filterText && this.filterText.trim().length > 0) {
      this.filteredFolders = this.projectManager.folders.filter(f => {
        const containsSearch = f.name.toLowerCase().includes(this.filterText.toLowerCase());
        const containsProject = f.projects.some(p => p.name.toLowerCase().includes(this.filterText.toLowerCase()));

        return containsProject || containsSearch;
      });
    } else {
      this.filteredFolders = this.projectManager.folders;
    }
  }

  private handleTargetSpecError(error: any): void {
    const body = error.error;
    if (body?.deprecated_interests?.length) {
      const rows = body.deprecated_interests
        .map((i: any) => `<li><b>${i.invalid_name}</b> → ${i.suggested_name}</li>`)
        .join('');
      this.alert.notify('Invalid interests', `${body.message}<ul>${rows}</ul>`, 'error', {enableHtml: true, disableTimeOut: true, tapToDismiss: true});
    } else {
      this.alert.notify('Error', body?.message || error.message, 'error');
    }
  }

  private canLoadProject() {
    const projectCountCheck = this.projects.length < this.projectCount;
    return !this.loader.loading && projectCountCheck && !this.openPopup && !this.lockScrollListener;
  }

  setProjectMapVersion(project: Project, version: number) {
    this.apiProject.projectMapVersionUpdate(project, version).then(async p => {
      await this.projectManager.addProjectAtFirstPosition(p);
    });
  }

  clearProjectCache(project: Project) {
    this.apiProject.projectCacheClear(project).then(result => {
      if (result) {
        this.alert.notify("Cache clear", "Project cache cleared!", "success");
        this.clearProjectLocalCache(project);
      }
      else this.alert.notify("Cache clear", "Project cache could not be cleared...", "error");
    }).catch(err => {
      this.alert.notify("Cache clear", "Project cache could not be cleared : " + err.error.message, "error");
    });
  }

  clearProjectLocalCache(project: Project) {
    this.projectManager.clearProjectData(project);
  }

  projectCrawlStatus(project: Project) {
    const target = project.audience_target;
    const bench = project.audience_bench;
    let chosenStatus: CrawlStatus;

    if (target.status && bench.status) {
      if (target.status.actualStep == 0) chosenStatus = bench.status;
      else if (bench.status.actualStep == 0) chosenStatus = target.status;
      else {
        chosenStatus = target.status.actualStep <= bench.status.actualStep ? target.status : bench.status;
      }
      if (chosenStatus.progression) chosenStatus.loadingStatus = `${chosenStatus.actualStep}/${chosenStatus.steps - 1} - ${chosenStatus.state}`;
      else if (!chosenStatus.loadingStatus) chosenStatus.loadingStatus = chosenStatus.state;
    }

    return chosenStatus;
  }

  goToReporting(project: Project) {
    this.tracker.track('report_list_go_to_reporting_button_clicked', {
      report_id: project.id,
    });

    this.router.navigate(['/dashboard/reporting/template/' + project.id]).then();
  }

  exportFolderCsv(folder: Folder) {
    this.apiData.getFolderNormalizedDataCSV(folder).then(data => {
      this.dialog.open(SpModalInfoComponent, {
        maxWidth: '372px',
        data: {
          content: 'The CSV Export of your folder has been launched successfully. You should receive an email in a few minutes.',
          icon: 'icon_sp_rocket',
          title: 'CSV Export Launched'
        }
      });
    });
  }

  /// Getters & Setters
  get projects(): Project[] {
    return this.projectManager.projects ? this.projectManager.projects : [];
  }
  get chunkLoading(): boolean {
    return this.projectManager.chunkLoading;
  }
  get projectCount(): number {
    return this.projectManager.projectCount;
  }
  get folders(): AnyFolder[] {
    return this.projectManager.openedFolder ? [] : this.filteredFolders;
  }

  set projectAndFolderTypeFiltering(value: string[]) {
    this.projectManager.projectAndFolderTypeFiltering = value;
  }

  get projectAndFolderTypeFiltering(): string[] {
    return this.projectManager.projectAndFolderTypeFiltering;
  }

  get env() { return environment; }
}
